import $ from 'jquery'
import {Parser, type ParserT, nullable, string, firstOf, mapStatic, date} from '@freckle/parser'

export type OperationLocationT = string
type JQueryXHR = ReturnType<(typeof $)['ajax']>
export type RetryAfterT =
  | {
      tag: 'http-date'
      contents: Date
    }
  | {
      tag: 'delay-seconds'
      contents: number
    }

// N.B.: Currently we're only using delay-seconds; extra work is required to distinguish between date and string
const parseRetryAfter = firstOf<RetryAfterT>(
  mapStatic(string(), delaySeconds => ({
    tag: 'delay-seconds',
    contents: parseInt(delaySeconds, 10)
  })),
  mapStatic(date(), httpDate => ({tag: 'http-date', contents: httpDate}))
)

type InitialOperationResponse<T> = {
  response: T
  retryAfter: RetryAfterT
  operationLocation: OperationLocationT
}

class MissingOperationResponseHeaderError extends Error {
  expectedHeader: string
  url: string
  constructor(expectedHeader: string, url: string) {
    super(
      `Expected header "${expectedHeader}" was not returned in by operation kickoff API: #{url}`
    )
    this.name = 'MissingOperationResponseHeaderError'
    this.expectedHeader = expectedHeader
    this.url = url
  }
}

const parseOperationResponse = <TResponse>(
  request: JQueryXHR,
  url: string,
  responseParser: ParserT<TResponse>
): Promise<InitialOperationResponse<TResponse>> =>
  new Promise((resolve, reject) => {
    request
      .then((response, _textStatus, jqXHR) => {
        const requireHeader = (header: string) => {
          const value = jqXHR.getResponseHeader(header)
          if (typeof value !== 'string') {
            throw new MissingOperationResponseHeaderError(header, url)
          }
          return value
        }

        try {
          resolve({
            response: Parser.run(response, responseParser),
            retryAfter: Parser.run(requireHeader('Retry-After'), parseRetryAfter),
            operationLocation: requireHeader('Operation-Location')
          })
        } catch (error) {
          reject(error)
        }
      })
      .fail(jqXHR => {
        reject(jqXHR)
      })
  })

export const startOperationForm = <TResponse>(
  method: 'DELETE' | 'PUT' | 'POST' | 'PATCH',
  url: string,
  responseParser: ParserT<TResponse>,
  data: FormData
): Promise<InitialOperationResponse<TResponse>> =>
  parseOperationResponse(
    $.ajax({
      url,
      type: method,
      data,
      contentType: false,
      processData: false,
      timeout: 0,
      xhrFields: {
        withCredentials: true
      }
    }),
    url,
    responseParser
  )

export const startOperation = <TResponse>(
  method: 'DELETE' | 'PUT' | 'POST' | 'PATCH',
  url: string,
  responseParser: ParserT<TResponse>,
  data?: string | Object
): Promise<InitialOperationResponse<TResponse>> =>
  parseOperationResponse(
    $.ajax({
      url,
      type: method,
      contentType: 'application/json; charset=utf-8',
      dataType: 'json',
      data,
      xhrFields: {
        withCredentials: true
      }
    }),
    url,
    responseParser
  )

type ResponseWithRetryAfter<T> = {
  response: T
  retryAfter: RetryAfterT | undefined | null
}

export function fetchWithRetryAfter<T>(
  url: OperationLocationT,
  responseParser: ParserT<T>
): Promise<ResponseWithRetryAfter<T>> {
  return new Promise((resolve, reject) => {
    $.ajax({
      url,
      type: 'GET',
      xhrFields: {
        withCredentials: true
      }
    })
      .then((response, _textStatus, jqXHR) => {
        try {
          const retryAfter = jqXHR.getResponseHeader('Retry-After')
          resolve({
            response: Parser.run(response, responseParser),
            retryAfter: Parser.run(retryAfter, nullable(parseRetryAfter))
          })
        } catch (error) {
          reject(error)
        }
      })
      .fail((_jqXHR, _textStatus, errorThrown) => {
        reject(new Error(errorThrown))
      })
  })
}
